<template>
  <div>
    <Head title="Unidades de Transporte" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Unidades de Transporte</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Create Button -->
        <Link 
          href="/transport-units/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          Nueva Unidad
        </Link>
      </div>
    </div>

    <!-- Search Tool Bar -->
    <div class="flex gap-4 mb-6">
      <div class="relative flex-1">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <icon name="search" class="w-4 h-4 text-gray-400" />
        </div>
        <input 
          v-model="form.search.terms" 
          type="text" 
          class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors shadow-sm" 
          placeholder="Buscar por patente, chofer o transportista..."
        />
      </div>
    </div>

    <!-- Content Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Mobile View -->
      <div class="sm:hidden divide-y divide-gray-100">
        <div v-for="unit in transportUnits.data" :key="unit.id" class="p-4 hover:bg-gray-50 transition-colors">
          <Link :href="`transport-units/${unit.id}/edit`" class="block space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center overflow-hidden flex-shrink-0 border border-orange-100">
                <i class="fa fa-car text-orange-400 text-xl" />
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <h3 class="font-bold text-gray-900 line-clamp-1">{{ unit.license_plate }}</h3>
                  <span class="ml-2 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-bold">
                    {{ unit.type || 'Vehículo' }}
                  </span>
                </div>
                <p class="text-sm text-orange-600 font-medium mt-1">{{ unit.carrier?.name }}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-2 text-sm text-gray-600 border-t border-gray-100 pt-2">
              <div>
                <span class="block text-xs text-gray-400">Chofer</span>
                <span class="font-medium text-gray-800">{{ unit.driver_name || 'No asignado' }}</span>
              </div>
              <div>
                <span class="block text-xs text-gray-400">Capacidad (Kg)</span>
                <span class="font-medium text-gray-800">{{ unit.capacity_kg || 'N/A' }}</span>
              </div>
            </div>
          </Link>
        </div>
      </div>

      <!-- Desktop View -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/50 text-left border-b border-gray-100">
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Patente / Matrícula</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Transportista</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Tipo</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Chofer Asignado</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Cap. (Kg)</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Cap. (Vol)</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="unit in transportUnits.data" 
              :key="unit.id"
              class="group hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4">
                <Link :href="`transport-units/${unit.id}/edit`" class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center border border-orange-100">
                    <i class="fa fa-car text-orange-400 text-sm" />
                  </div>
                  <span class="text-gray-900 font-bold hover:text-orange-600 uppercase">{{ unit.license_plate }}</span>
                </Link>
              </td>
              
              <td class="px-6 py-4">
                <Link :href="`carriers/${unit.carrier_id}/edit`" class="text-orange-600 font-medium hover:underline">
                  {{ unit.carrier?.name }}
                </Link>
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ unit.type || 'N/A' }}
              </td>

              <td class="px-6 py-4 text-gray-600 text-sm">
                {{ unit.driver_name || 'No asignado' }}
              </td>

              <td class="px-6 py-4 text-right text-gray-600 text-sm">
                {{ unit.capacity_kg ? unit.capacity_kg + ' kg' : 'N/A' }}
              </td>

              <td class="px-6 py-4 text-right text-gray-600 text-sm">
                {{ unit.capacity_volume ? unit.capacity_volume + ' m³' : 'N/A' }}
              </td>

              <td class="px-6 py-4 text-right">
                <Link 
                  :href="`transport-units/${unit.id}/edit`" 
                  class="text-gray-400 hover:text-orange-500 transition-colors mx-2"
                >
                  <i class="fa fa-edit" />
                </Link>
              </td>
            </tr>

            <tr v-if="!transportUnits?.data?.length">
              <td colspan="7" class="py-20 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-car text-2xl opacity-50" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-1">No hay unidades registradas</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Empieza por crear un vehículo (furgón, camión) para realizar tus despachos.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <div class="mt-6 flex justify-center">
      <pagination 
        v-if="transportUnits?.meta?.links"
        :links="transportUnits.meta.links"
        :query-params="currentQueryParams"
      />
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination.vue'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'TransportUnitsIndex',
  
  components: {
    Pagination,
    Head,
    Icon,
    Link,
  },

  layout: Layout,

  props: {
    transportUnits: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: this.$inertia.form({
        search: {
          terms: '',
        },
      }),
    }
  },

  computed: {
    currentQueryParams() {
      return { search: pickBy(this.form.search) }
    },
  },

  watch: {
    'form.search': {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/transport-units',
          { search: pickBy(this.form.search), page: 1 },
          { preserveState: true },
        )
      }, 300),
    },
  },
}
</script>
